// pages/AdminComments.js - Admin dashboard for managing user comments
import React, { useState, useEffect } from 'react';
import { MessageSquare, XCircle, Trash2, Reply, Clock } from 'lucide-react';
import commentService from '../services/CommentService';

function AdminComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [notification, setNotification] = useState(null);

  // Fetch comments on component mount
  useEffect(() => {
    fetchComments();
  }, []);

  // Auto-hide notifications after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await commentService.getAllComments();

      if (result.success) {
        setComments(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment? This will also delete all replies. This action cannot be undone.')) {
      try {
        const result = await commentService.deleteComment(commentId);

        if (result.success) {
          showNotification('Comment deleted successfully!', 'success');
          // Refresh comments
          await fetchComments();
        } else {
          showNotification('Failed to delete comment: ' + result.error, 'error');
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
        showNotification('Failed to delete comment', 'error');
      }
    }
  };

  const handleReplyClick = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
  };

  const handleReplySubmit = async (commentId) => {
    if (!replyText.trim()) {
      showNotification('Please enter a reply message', 'error');
      return;
    }

    try {
      setSubmittingReply(true);
      
      const result = await commentService.addReply(
        commentId, 
        replyText, 
        'Admin' // In a real app, this would be the logged-in admin's name
      );

      if (result.success) {
        showNotification('Reply added successfully!', 'success');
        setReplyingTo(null);
        setReplyText('');
        // Refresh comments to show new reply
        await fetchComments();
      } else {
        showNotification('Failed to add reply: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      showNotification('Failed to add reply', 'error');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-sm font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-3 text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <MessageSquare className="mr-3 text-blue-600" size={28} />
                Comment Management
              </h1>
              <p className="mt-1 text-gray-600">
                Manage user comments and provide admin responses
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                Total Comments: <span className="font-bold text-gray-900">{comments.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading comments...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Failed to Load Comments</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Comments Table */}
      {!loading && !error && (
        <div className="container mx-auto px-4 pb-8">
          {comments.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Comments Found</h3>
              <p className="text-gray-500">No comments have been submitted yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                        Comment Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Author & Content
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Replies
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comments.map((comment) => (
                      <React.Fragment key={comment.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 w-2/5">
                            <div className="text-sm text-gray-900">
                              <p className="font-medium line-clamp-3" title={comment.text}>
                                {comment.text}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{comment.authorName}</div>
                              <div className="text-gray-500">{comment.relatedContent}</div>
                              {comment.authorEmail && (
                                <div className="text-xs text-gray-400">{comment.authorEmail}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col space-y-2">
                              <div className="text-xs text-gray-500">
                                {formatDate(comment.createdAt)}
                              </div>
                              <div className="flex items-center text-xs text-blue-600">
                                <Reply className="w-3 h-3 mr-1" />
                                {comment.replies?.length || 0} replies
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center space-x-2">
                              <button 
                                onClick={() => handleReplyClick(comment.id)}
                                className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                              >
                                <Reply className="w-3 h-3 mr-1" />
                                Reply
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteComment(comment.id);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-red-300 rounded-full text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Reply Form Row */}
                        {replyingTo === comment.id && (
                          <tr className="bg-blue-50">
                            <td colSpan="4" className="px-6 py-4">
                              <div className="max-w-2xl">
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                      <Reply className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 mb-2">
                                      Reply as Admin
                                    </div>
                                    <textarea
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
                                      rows="3"
                                      placeholder="Type your reply here..."
                                      maxLength="1000"
                                    />
                                    <div className="flex items-center justify-between mt-2">
                                      <div className="text-xs text-gray-500">
                                        {replyText.length}/1000 characters
                                      </div>
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={handleCancelReply}
                                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleReplySubmit(comment.id)}
                                          disabled={submittingReply || !replyText.trim()}
                                          className="px-4 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {submittingReply ? 'Sending...' : 'Send Reply'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Existing Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <tr className="bg-gray-50">
                            <td colSpan="4" className="px-6 py-4">
                              <div className="space-y-3">
                                <div className="text-sm font-medium text-gray-700 flex items-center">
                                  <Reply className="w-4 h-4 mr-2" />
                                  Admin Replies ({comment.replies.length})
                                </div>
                                {comment.replies.map((reply) => (
                                  <div key={reply.id} className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-start space-x-3">
                                      <div className="flex-shrink-0">
                                        <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                          <span className="text-xs text-white font-bold">A</span>
                                        </div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="text-sm font-medium text-green-700">
                                            {reply.adminName}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {formatDate(reply.createdAt)}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-900">{reply.text}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminComments;